import CompanionModal from "@components/companion/CompanionModal";
import {
  DSCardWrapper,
  type DSCardWrapperVariant,
} from "@components/design-system/molecules";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBrain,
  faGauge,
  faLifeRing,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  type CompanionEffort,
  isCompanionEffortEnabled,
} from "@hooks/use-companion-v2";
import { useFlags } from "launchdarkly-react-client-sdk";

interface EffortOption {
  value: CompanionEffort;
  title: string;
  tagline: string;
  examples: string[];
  dataTestId: string;
  icon: IconProp;
  variant?: DSCardWrapperVariant;
}

const OPTIONS: EffortOption[] = [
  {
    value: "high",
    title: "Default",
    icon: faBrain,
    tagline:
      "Recommended - All Companions will be able to answer or complete tasks, and will have a better understanding of your notes and context.",
    examples: [
      "Doing deep research on a complex topic",
      "Comparing treatment plans across multiple visits",
      "Synthesizing a long patient history",
      "Helping you complete actions in Scribenote",
    ],
    dataTestId: "effort-option-high",
  },
  {
    value: "standard",
    title: "Fast",
    icon: faGauge,
    tagline: "Faster answers and fewer Treats.",
    examples: [
      "Looking up a past visit",
      "Summarizing a recent note",
      "Drafting a client email",
    ],
    dataTestId: "effort-option-standard",
  },
  {
    value: "support",
    title: "Free Support",
    icon: faLifeRing,
    tagline:
      "Otto will help answer simple questions about using Scribenote. This will not cost any Treats, but Otto will not have intelligent context on what is happening in your notes or account.",
    examples: [],
    dataTestId: "effort-option-support",
    variant: "ghost",
  },
];

export interface EffortPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (effort: CompanionEffort) => void;
}

export const EffortPickerModal = ({
  isOpen,
  onClose,
  onSelect,
}: EffortPickerModalProps) => {
  const {
    allowCompanionOptIn,
    showGlobalCompanionTierLow,
    showGlobalCompanionTierHigh,
  } = useFlags();

  if (!isOpen) return null;

  const tierFlags = {
    showGlobalCompanionTierLow,
    showGlobalCompanionTierHigh,
  };

  const handlePick = (effort: CompanionEffort) => {
    onSelect(effort);
    onClose();
  };

  const visibleOptions = OPTIONS.filter((option) =>
    isCompanionEffortEnabled(
      option.value,
      tierFlags,
      allowCompanionOptIn,
    ),
  );

  return (
    <CompanionModal title="Start a new chat" onClose={onClose}>
      <div
        className="flex flex-col gap-md text-left"
        data-testid="effort-picker-modal"
      >
        <p className="text-secondary text-sm font-companion">
          Pick the type of chat session you want to have.
        </p>
        {visibleOptions.map((option) => (
          <DSCardWrapper
            key={option.value}
            interactive
            variant={option.variant}
            onClick={() => handlePick(option.value)}
            aria-label={`Select ${option.title}`}
            dataTestId={option.dataTestId}
          >
            <div className="flex flex-col gap-xs">
              <div className="flex flex-row items-center gap-xs">
                <h4 className="font-companion">{option.title}</h4>{" "}
                <FontAwesomeIcon icon={option.icon} />
              </div>
              <p className="text-sm text-secondary font-companion">
                {option.tagline}
              </p>
              {option.examples.length > 0 && (
                <ul className="list-disc pl-md text-xs text-secondary">
                  {option.examples.map((example) => (
                    <li key={example}>{example}</li>
                  ))}
                </ul>
              )}
            </div>
          </DSCardWrapper>
        ))}
      </div>
      <p className="text-xs italic text-secondary font-companion p-md">
        Companions run on AI models which can sometimes make mistakes.
        Companion responses should never replace professional clinical
        judgement.
      </p>
    </CompanionModal>
  );
};

EffortPickerModal.displayName = "EffortPickerModal";
